// React
import React, { useEffect, useMemo, useRef, useState } from "react";

// React Router
import { useLocation } from "react-router-dom";

// MUI Components
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useTheme } from "@mui/material/styles";

// MUI Icons
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BrowserUpdatedIcon from "@mui/icons-material/BrowserUpdated";
import EthernetIcon from "@mui/icons-material/Cable";
import ComputerIcon from "@mui/icons-material/Computer";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DevicesIcon from "@mui/icons-material/Devices";
import ModeIcon from "@mui/icons-material/Mode";
import GSMIcon from "@mui/icons-material/NetworkCell";
import WifiIcon from "@mui/icons-material/NetworkWifi";
import SignalCellular1BarIcon from "@mui/icons-material/SignalCellular1Bar";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import SignalCellularAlt2BarIcon from "@mui/icons-material/SignalCellularAlt2Bar";

// i18n
import { useTranslation } from "react-i18next";

// External Libraries
import { v4 as uuidv4 } from "uuid";

// API
import { postCommand } from "../../../api/commandAPI";
import { getDevices, isNodeDeviceOnline } from "../../../api/deviceAPI";
import { getProjectDetailStatuses } from "../../../api/projectAPI";
import postStatus from "../../../api/statusAPI";

// Utils & Config
import { useRefreshInterval } from "../../../contexts/RefreshIntervalContext";
import { usePageVisibility } from "../../../hooks/usePageVisibility";
import { ConvertOIDValueToString } from "../../../utils/objectConverter";
import { getPlaceIdFromPathname } from "../../../utils/pathUtils";
import { useObjectData } from "../../../hooks/useLocalStorageData";
import { useSnackbar } from "../../../hooks/useSnackbar";

export default function NodeTag() {
  const { t } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const [groupedData, setGroupedData] = useState([]);
  const [onlineStatus, setOnlineStatus] = useState({});
  const [summaryDetailOrder, setSummaryDetailOrder] = useState([]);
  const { interval, isRunning } = useRefreshInterval();
  const isPageVisibility = usePageVisibility();
  // Use custom hook to get objectData from localStorage
  const objectData = useObjectData();

  const rawId = getPlaceIdFromPathname(location.pathname);
  const node_id = rawId ? parseInt(rawId, 10) : null;

  const latestNodeId = useRef(node_id);
  const latestObjectData = useRef(objectData);
  const commandKeyRef = useRef(uuidv4());
  const cmdStr = "updateStatus";
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();
  const [dialogOpenForId, setDialogOpenForId] = useState(null);

  // Update refs on every render
  latestNodeId.current = node_id;
  latestObjectData.current = objectData;

  useEffect(() => {
    (async () => {
      try {
        const response = await getProjectDetailStatuses();
        // Axios interceptor returns response.data.data if returnCode === 0, otherwise returns full response
        const result = Array.isArray(response) ? response : response?.data;
        // API returns an array of codes as strings
        if (
          Array.isArray(result) &&
          result.every((x) => typeof x === "string")
        ) {
          setSummaryDetailOrder(result);
        }
      } catch (e) {
        console.error("getProjectDetailStatuses API call failed:", e);
      }
    })();
  }, []);


  const handleOpenDialog = (id) => setDialogOpenForId(id);
  const handleCloseDialog = () => setDialogOpenForId(null);

  const handleCopy = async (text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text.toString());
      const ipv4Regex = /^\d{1,3}(?:\.\d{1,3}){3}$/;
      const isIp = ipv4Regex.test(text);
      showSnackbar(
        isIp ? "IP adresi panoya kopyalandı" : "Değer panoya kopyalandı",
        "success"
      );
    } catch (err) {
      showSnackbar("Panoya kopyalama başarısız", "error");
    }
  };

  const initialRequestsSent = useRef(false);

  useEffect(() => {
    let intervalId = null;
    let commandTimeoutId = null;

    async function fetchStatus(currentNodeId, currentObjectData) {
      const baseAllowedObjectCodes = [
        "2",
        "3",
        "4",
        "6",
        "7",
        "8",
        "12",
        "15",
        "16",
        "17",
        "18",
        "19",
        "67",
        "68",
        "69",
        "70",
      ];
      const allowedObjectCodes = Array.from(
        new Set([...baseAllowedObjectCodes, ...summaryDetailOrder])
      );

      const nodeList = currentNodeId ? [currentNodeId] : null;
      const response = await postStatus({
        objectCodeList: allowedObjectCodes,
        nodeList,
      });
      // Axios interceptor returns response.data.data if returnCode === 0, otherwise returns full response
      const result = Array.isArray(response) ? response : response?.data;

      if (result && Array.isArray(result)) {
        const filteredByObjectCode = result;
        const groupByDType = (data) => {
          const groups = {};
          data.forEach((item) => {
            if (!groups[item.dType]) {
              groups[item.dType] = [];
            }
            groups[item.dType].push(item);
          });

          const result = Object.values(groups).map((group) => {
            const pairsObject = {};
            group.forEach((item) => {
              pairsObject[item.objectCode] = item.value;
            });
            return pairsObject;
          });

          return result;
        };

        const grouped = groupByDType(filteredByObjectCode);
        const processedData = grouped.map((dataObj, index) => {
          const processedObj = {};
          processedObj.id = index + 1;

          for (const [objectCode, value] of Object.entries(dataObj)) {
            const objectDef = currentObjectData.find(
              (item) => item.code === parseInt(objectCode, 10)
            );

            if (objectDef) {
              const objectOID = { Type: objectDef.type };
              const convertedValue = ConvertOIDValueToString(objectOID, value);
              processedObj[objectCode] = {
                value: convertedValue,
                name: objectDef.name,
                nonConvertedValue: value,
              };
            } else {
              processedObj[objectCode] = {
                value: value,
                name: "UNKNOWN",
                nonConvertedValue: value,
              };
            }
          }
          return processedObj;
        });
        setGroupedData(processedData);
        async function fetchOnlineStatus(devices) {
          const statusObj = {};
          await Promise.all(
            devices.map(async (device) => {
              const serial = device["2"]?.value;
              if (serial) {
                try {
                  const response = await isNodeDeviceOnline(serial);
                  // Axios interceptor might return data directly or full response
                  const isOnline = typeof response === 'boolean' ? response : response?.data;
                  statusObj[serial] = isOnline;
                } catch (e) {
                  console.error("[NodeTag] Error fetching online status for serial:", serial, e);
                  statusObj[serial] = false;
                }
              }
            })
          );
          setOnlineStatus(statusObj);
        }
        fetchOnlineStatus(processedData);
      }
    }

    if (!initialRequestsSent.current) {
      fetchStatus(latestNodeId.current, latestObjectData.current);
      initialRequestsSent.current = true;
    }

    if (isRunning && isPageVisibility) {
      let commandSent = false;
      const commandTimeoutMs = Math.max((interval - 5) * 1000, 0);
      commandTimeoutId = setTimeout(() => {
        sendCommandToDevice(node_id, commandKeyRef.current, cmdStr);
        commandSent = true;
      }, commandTimeoutMs);
      intervalId = setInterval(() => {
        fetchStatus(latestNodeId.current, latestObjectData.current);
        commandSent = false;
        const commandTimeoutMsInner = Math.max((interval - 5) * 1000, 0);
        commandTimeoutId = setTimeout(() => {
          if (!commandSent) {
            sendCommandToDevice(node_id, commandKeyRef.current, cmdStr);
            commandSent = true;
          }
        }, commandTimeoutMsInner);
      }, interval * 1000);
    }

    async function sendCommandToDevice(nodeId, cmdKey, cmdStr) {
      try {
        const devicesResponse = await getDevices();
        if (devicesResponse && devicesResponse.data) {
          const devices = devicesResponse.data;
          const filteredDevices = devices.filter(
            (device) => device.node_idNode === nodeId
          );
          const targetDevice = filteredDevices[0];
          await postCommand(nodeId, targetDevice.idDevice, cmdKey, cmdStr, []);
        }
      } catch (error) {
        console.error("Error sending command to device:", error);
      }
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
      if (commandTimeoutId) clearTimeout(commandTimeoutId);
    };
  }, [node_id, interval, isRunning, summaryDetailOrder]);

  return (
    <Grid container spacing={2} columns={12}>
      {groupedData.map((device) => {
        let ConnectionIcon;
        if (device["18"]?.value === "WIFI") {
          ConnectionIcon = WifiIcon;
        } else if (device["18"]?.value === "GSM") {
          ConnectionIcon = GSMIcon;
        } else {
          ConnectionIcon = EthernetIcon;
        }

        const levelVal = device["15"]?.value;
        let LevelIcon;
        if (levelVal != null) {
          if (levelVal < 35) {
            LevelIcon = SignalCellular1BarIcon;
          } else if (levelVal < 70) {
            LevelIcon = SignalCellularAlt2BarIcon;
          } else {
            LevelIcon = SignalCellularAltIcon;
          }
        }

        const levelVall = device["12"]?.value;
        let LevelIconn;
        if (levelVall != null) {
          if (levelVall < 35) {
            LevelIconn = SignalCellular1BarIcon;
          } else if (levelVall < 70) {
            LevelIconn = SignalCellularAlt2BarIcon;
          } else {
            LevelIconn = SignalCellularAltIcon;
          }
        }

        const serial = device["2"]?.value;
        // console.log("Device Connection Type:", device["18"].value);

        // console.log("Device Serial:", serial);
        const isOnline = serial ? onlineStatus[serial] : false;
        // connection detail value: GSM -> object 68, WIFI -> object 67, other -> object 69
        const connectionDetail =
          device["18"]?.value === "GSM"
            ? device["68"]?.value ?? "-"
            : device["18"]?.value === "WIFI"
            ? device["67"]?.value ?? "-"
            : device["69"]?.value ?? "-";
        // console.log("isOnline for serial", serial, ":", isOnline);
        const result = (
          <React.Fragment key={device.id}>
            <Grid size={{ xs: 12, sm: 6, lg: 12 }}>
              <Card sx={{ height: "100%", border: "2px solid #e0e0e0" }}>
                <CardContent>
                  {" "}
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={3}
                    alignItems={{ xs: "flex-start", md: "center" }}
                    justifyContent="flex-start"
                    flexWrap="wrap"
                    sx={{ gap: 2 }}
                  >
                    <Stack
                      alignItems="center"
                      spacing={1}
                      sx={{ mr: { xs: 0, md: 2 }, mb: { xs: 2, md: 0 } }}
                    >
                      <Box
                        sx={{
                          bgcolor: isOnline ? "#4caf50" : "red",
                          color: "white",
                          p: 1,
                          borderRadius: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minWidth: "45px",
                        }}
                      >
                        <ComputerIcon />
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.75rem",
                          textAlign: "center",
                        }}
                      >
                        Device Serial <br />
                        {/* {device["6"]?.value ?? "N/A"} /{" "}
                        {device["4"]?.value ?? "N/A"} */}
                        {device["2"]?.value}
                      </Typography>
                    </Stack>
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{
                        display: { xs: "none", lg: "block" },
                        height: "72px",
                        mx: 1,
                        alignSelf: "center",
                        border: "1px solid #e0e0e0",
                      }}
                    />
                    <Box
                      sx={{ flexGrow: 1, display: { xs: "none", md: "block" } }}
                    />
                    {device["19"]?.value != null && (
                      <Card
                        sx={{
                          width: { xs: "100%", sm: "150px" },
                          height: "72px",
                          border: "2px solid #e0e0e0",
                        }}
                      >
                        <CardContent>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <ModeIcon />
                            <Typography
                              variant="body2"
                              title={device["19"].value}
                              sx={{
                                textAlign: "center",
                                width: "100%",
                                fontSize: "0.8rem",
                              }}
                            >
                              <span style={{ fontWeight: "bold" }}>
                                {t("nodeTag.mode")}
                              </span>{" "}
                              {device["19"].value &&
                              device["19"].value.length > 8
                                ? device["19"].value.substring(0, 8) + "...."
                                : device["19"].value}
                            </Typography>
                          </Stack>
                        </CardContent>
                      </Card>
                    )}{" "}
                    {device["3"]?.value != null && (
                      <Card
                        sx={{
                          width: { xs: "100%", sm: "150px" },
                          height: "72px",
                          border: "2px solid #e0e0e0",
                        }}
                      >
                        <CardContent>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <DevicesIcon />
                            <Typography
                              variant="body2"
                              sx={{
                                textAlign: "center",
                                width: "100%",
                                fontSize: "0.8rem",
                              }}
                            >
                              <span style={{ fontWeight: "bold" }}>
                                {t("nodeTag.type")}
                              </span>{" "}
                              {device["3"].value}
                            </Typography>
                          </Stack>
                        </CardContent>
                      </Card>
                    )}{" "}
                    {device["7"]?.value != null && (
                      <Card
                        sx={{
                          width: { xs: "100%", sm: "150px" },
                          height: "72px",
                          border: "2px solid #e0e0e0",
                        }}
                      >
                        <CardContent>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <AccessTimeIcon />
                            <Typography
                              variant="body2"
                              sx={{
                                textAlign: "center",
                                width: "100%",
                                fontSize: "0.8rem",
                              }}
                            >
                              <span style={{ fontWeight: "bold" }}>
                                {t("nodeTag.uptime")}
                              </span>{" "}
                              {device["7"] && device["7"].value
                                ? device["7"].value
                                : t("nodeTag.na")}
                            </Typography>
                          </Stack>
                        </CardContent>
                      </Card>
                    )}{" "}
                    {device["8"]?.value != null && (
                      <Card
                        sx={{
                          width: { xs: "100%", sm: "150px" },
                          height: "72px",
                          border: "2px solid #e0e0e0",
                        }}
                      >
                        <CardContent>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <BrowserUpdatedIcon />
                            <Typography
                              variant="body2"
                              sx={{
                                textAlign: "center",
                                width: "100%",
                                fontSize: "0.8rem",
                              }}
                            >
                              <span style={{ fontWeight: "bold" }}>
                                {t("nodeTag.softwareUpdate")}
                              </span>{" "}
                              {device["8"] && device["8"].value
                                ? device["8"].value
                                : t("nodeTag.na")}
                            </Typography>
                          </Stack>
                        </CardContent>
                      </Card>
                    )}{" "}
                    {device["18"]?.value != null && (
                      <Card
                        onClick={() => handleCopy(connectionDetail)}
                        sx={{
                          width: { xs: "100%", sm: "150px" },
                          height: "72px",
                          border: "2px solid #e0e0e0",
                          cursor: "pointer",
                        }}
                      >
                        <CardContent>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="flex-start"
                            sx={{ pt: 0 }}
                          >
                            <ConnectionIcon />
                            <Typography
                              variant="body2"
                              sx={{
                                textAlign: "center",
                                width: "100%",
                                fontSize: "0.8rem",
                              }}
                            >
                              <span
                                style={{
                                  fontWeight: "bold",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {t("nodeTag.connection")} {device["18"].value}
                              </span>
                              <br />
                              <span
                                style={{
                                  fontSize: "0.75rem",
                                  color:
                                    theme &&
                                    theme.palette &&
                                    theme.palette.mode === "dark"
                                      ? "#fff"
                                      : "#000",
                                }}
                              >
                                {connectionDetail}
                              </span>
                            </Typography>
                          </Stack>
                        </CardContent>
                      </Card>
                    )}{" "}
                    {device["15"]?.value != null && (
                      <Card
                        sx={{
                          width: { xs: "100%", sm: "150px" },
                          height: "72px",
                          border: "2px solid #e0e0e0",
                        }}
                      >
                        <CardContent>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <LevelIcon />
                            <Typography
                              variant="body2"
                              sx={{
                                textAlign: "center",
                                width: "100%",
                                fontSize: "0.8rem",
                              }}
                            >
                              <span style={{ fontWeight: "bold" }}>
                                {t("nodeTag.wifiLevel")}
                              </span>{" "}
                              {device["15"].value}
                            </Typography>
                          </Stack>
                        </CardContent>
                      </Card>
                    )}{" "}
                    {device["12"]?.value != null && (
                      <Card
                        sx={{
                          width: { xs: "100%", sm: "150px" },
                          height: "72px",
                          border: "2px solid #e0e0e0",
                        }}
                      >
                        <CardContent>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <LevelIconn />
                            <Typography
                              variant="body2"
                              sx={{
                                textAlign: "center",
                                width: "100%",
                                fontSize: "0.8rem",
                              }}
                            >
                              <span style={{ fontWeight: "bold" }}>
                                {t("nodeTag.gsmLevel")}
                              </span>{" "}
                              {device["12"].value}
                            </Typography>
                          </Stack>
                        </CardContent>
                      </Card>
                    )}
                    {device["70"]?.value != null && (
                      <Card
                        sx={{
                          width: { xs: "100%", sm: "150px" },
                          height: "72px",
                          border: "2px solid #e0e0e0",
                        }}
                      >
                        <CardContent>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <BrowserUpdatedIcon />
                            <Typography
                              variant="body2"
                              sx={{
                                textAlign: "center",
                                width: "100%",
                                fontSize: "0.8rem",
                              }}
                            >
                              <span style={{ fontWeight: "bold" }}>
                                MDB FW
                              </span>{" "}
                              {device["70"].value}
                            </Typography>
                          </Stack>
                        </CardContent>
                      </Card>
                    )}
                    <Box
                      sx={{ flexGrow: 1, display: { xs: "none", md: "block" } }}
                    />
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{
                        display: { xs: "none", lg: "block" },
                        height: "72px",
                        mx: 1,
                        alignSelf: "center",
                        border: "1px solid #e0e0e0",
                      }}
                    />
                    <Stack direction="column" spacing={1}>
                      {(() => {
                        // Only show details for object codes "16" and "17" if they exist on the device
                        const details = ["16", "17"].filter(
                          (code) => device?.[code]?.value != null
                        );

                        // Render each detail line dynamically
                        return details.map((code) => {
                          const label = device?.[code]?.name ?? code;
                          const value = device?.[code]?.value;
                          return (
                            <Typography
                              key={code}
                              variant="body2"
                              title={String(value)}
                              onClick={() => handleCopy(value)}
                              sx={{
                                fontSize: "0.8rem",
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                cursor: "pointer",
                              }}
                            >
                              <span style={{ fontWeight: "bold" }}>
                                {label}:{" "}
                              </span>
                              {value}
                            </Typography>
                          );
                        });
                      })()}
                    </Stack>
                    {/* Small action button to open popup */}
                    <Box sx={{ ml: { md: "auto" } }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleOpenDialog(device.id)}
                      >
                        {t("nodeTag.more", { defaultValue: "Detay" })}
                      </Button>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            {/* Popup dialog */}
            <Dialog
              open={dialogOpenForId === device.id}
              onClose={handleCloseDialog}
              fullWidth
              maxWidth="md"
            >
              <DialogTitle>
                {t("nodeTag.dialogTitle", { defaultValue: "Cihaz Detayı" })}
              </DialogTitle>
              <DialogContent dividers>
                {/* Header chips */}
                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  sx={{ mb: 2 }}
                >
                  <Chip
                    size="small"
                    variant="outlined"
                    icon={<ComputerIcon fontSize="small" />}
                    label={`Serial: ${device["2"]?.value ?? "-"}`}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    icon={<ModeIcon fontSize="small" />}
                    label={`FirmWare / HardWare: ${
                      device["6"]?.value ?? "N/A"
                    } / ${device["4"]?.value ?? "N/A"}`}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    icon={<ConnectionIcon fontSize="small" />}
                    label={`${t("nodeTag.connection", {
                      defaultValue: "Bağlantı",
                    })}: ${device["18"]?.value ?? "-"}`}
                  />
                </Stack>
                {/* Details list */}
                {(() => {
                  const details = summaryDetailOrder.filter(
                    (code) => device?.[code]?.value != null
                  );

                  if (details.length === 0) {
                    return (
                      <Typography variant="body2">
                        {t("nodeTag.noDetails", {
                          defaultValue: "Gösterilecek detay bulunamadı",
                        })}
                      </Typography>
                    );
                  }

                  return (
                    <List dense sx={{ py: 0 }}>
                      {details.map((code) => {
                        const label = device?.[code]?.name ?? code;
                        const value = device?.[code]?.value;
                        return (
                          <ListItem
                            key={code}
                            divider
                            disableGutters
                            secondaryAction={
                              <Tooltip
                                title={t("common.copy", {
                                  defaultValue: "Kopyala",
                                })}
                              >
                                <IconButton
                                  edge="end"
                                  onClick={() => handleCopy(value)}
                                  size="small"
                                >
                                  <ContentCopyIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                            }
                          >
                            <ListItemText
                              primary={label}
                              primaryTypographyProps={{
                                sx: { fontWeight: 600 },
                              }}
                              secondary={
                                <Typography
                                  variant="body2"
                                  title={String(value)}
                                  onClick={() => handleCopy(value)}
                                  sx={{
                                    cursor: "pointer",
                                    fontFamily:
                                      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                  }}
                                >
                                  {value}
                                </Typography>
                              }
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  );
                })()}
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseDialog}>
                  {t("common.close", { defaultValue: "Kapat" })}
                </Button>
              </DialogActions>
            </Dialog>
            <Snackbar
              key={snackbar.message}
              open={snackbar.open}
              autoHideDuration={4000}
              onClose={hideSnackbar}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
              <Alert
                onClose={hideSnackbar}
                severity={snackbar.severity}
                sx={{ width: "100%" }}
                variant="filled"
                elevation={6}
              >
                {snackbar.message}
              </Alert>
            </Snackbar>
          </React.Fragment>
        );
        return result;
      })}
    </Grid>
  );
}
